/**
 * "Ask about this" (DNKADM-0463): describe a clicked page element by its UI text only.
 *
 * Captured: the element's role and accessible name, the nearest section heading, the
 * screen title (never the path, which carries member ids). NEVER captured: anything that can
 * be member data. The rules, each erring toward capturing nothing:
 *  - Inside a table or grid body cell the element's own text is never read; the cell's
 *    column header stands in for it. No readable header means the cell is not pickable.
 *  - A form field is described by its label, never its value or placeholder.
 *  - A name or heading is dropped when it holds an `@` or a digit (emails, amounts,
 *    dates, ids); it reads as data, not as a label.
 *  - Anything inside or holding `[data-ask-dink-private]`, or inside or holding a link to a
 *    member's detail page, is not pickable and stops the search (no fall-through to an
 *    ancestor). The same goes for a section heading or column header that is such an element.
 */

export interface AskDinkPick {
  /** The element's accessible name, or the column header for a data cell. */
  label: string;
  /** `button`, `tab`, `link`, `heading`, `badge`, `column`, ... */
  role: string;
  /** The nearest heading above the element, when it reads as a label. */
  section: string | null;
}

import { MEMBER_DETAIL } from './askDinkScreen';

/** Marks the panel itself; picking is off inside it. */
export const ASK_DINK_PANEL_ATTR = 'data-ask-dink-panel';
const PRIVATE_ATTR = 'data-ask-dink-private';

const MAX_LABEL = 60;
const MAX_CLIMB = 6;

const TAG_ROLES: Record<string, string> = {
  BUTTON: 'button',
  A: 'link',
  SELECT: 'combobox',
  TEXTAREA: 'textbox',
  H1: 'heading',
  H2: 'heading',
  H3: 'heading',
  H4: 'heading',
  H5: 'heading',
  H6: 'heading',
  TH: 'column header',
};

const INPUT_ROLES: Record<string, string> = {
  checkbox: 'checkbox',
  radio: 'radio button',
  button: 'button',
  submit: 'button',
  reset: 'button',
  text: 'textbox',
  search: 'textbox',
  email: 'textbox',
  tel: 'textbox',
  url: 'textbox',
  number: 'textbox',
  password: 'textbox',
};

const DATA_CELL = 'td, [role="gridcell"], [role="cell"]';

function isMemberLink(a: Element | null): boolean {
  const href = a?.getAttribute('href');
  if (href === null || href === undefined) return false;
  try {
    return MEMBER_DETAIL.test(new URL(href, window.location.origin).pathname);
  } catch {
    return false;
  }
}

/** True when `el` is inside, is, or holds member-identifying content. */
function isPrivate(el: Element): boolean {
  if (el.closest(`[${PRIVATE_ATTR}]`) !== null || el.querySelector(`[${PRIVATE_ATTR}]`) !== null) {
    return true;
  }
  if (isMemberLink(el.closest('a[href]'))) return true;
  return Array.from(el.querySelectorAll('a[href]')).some(isMemberLink);
}

/** A label is UI text only when short and free of anything that looks like data. */
function clean(raw: string | null | undefined): string | null {
  const text = (raw ?? '').replace(/\s+/g, ' ').trim();
  if (text === '' || text.length > MAX_LABEL) return null;
  if (/[@\d]/.test(text)) return null;
  return text;
}

function roleOf(el: Element): string | null {
  const explicit = el.getAttribute('role');
  if (explicit !== null && explicit !== 'presentation' && explicit !== 'none') {
    return explicit === 'columnheader' ? 'column header' : explicit;
  }
  if (el.tagName === 'INPUT') {
    return INPUT_ROLES[(el as HTMLInputElement).type] ?? null;
  }
  if (el.classList.contains('MuiChip-root')) return 'badge';
  return TAG_ROLES[el.tagName] ?? null;
}

/** The label text tied to a form control; never its value. */
function fieldName(el: Element): string | null {
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy !== null) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent ?? '')
      .join(' ');
    const named = clean(text);
    if (named !== null) return named;
  }
  const id = el.getAttribute('id');
  if (id !== null && id !== '') {
    for (const label of Array.from(el.ownerDocument.getElementsByTagName('label'))) {
      if (label.htmlFor === id) return clean(label.textContent);
    }
  }
  return clean(el.closest('label')?.textContent);
}

function isField(el: Element): boolean {
  return ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName);
}

function nameOf(el: Element): string | null {
  const aria = clean(el.getAttribute('aria-label'));
  if (aria !== null) return aria;
  if (isField(el)) {
    // A checkbox/radio's wrapping label is its name; a text field's value is data.
    return fieldName(el);
  }
  const named = fieldNameFromLabelledBy(el);
  if (named !== null) return named;
  if (el.tagName === 'IMG') return clean(el.getAttribute('alt'));
  return clean(el.textContent);
}

function fieldNameFromLabelledBy(el: Element): string | null {
  if (el.getAttribute('aria-labelledby') === null) return null;
  return fieldName(el);
}

/** The column header text for a body cell, or null when there is none to read. */
function columnHeaderFor(cell: Element): string | null {
  const row = cell.parentElement;
  if (row === null) return null;
  const table = cell.closest('table, [role="table"], [role="grid"], [role="treegrid"]');
  if (table === null) return null;
  const field = cell.getAttribute('data-field');
  if (field !== null) {
    const byField = Array.from(table.querySelectorAll('[role="columnheader"]')).find(
      (h) => h.getAttribute('data-field') === field,
    );
    if (byField !== undefined && !isPrivate(byField))
      return clean(byField.getAttribute('aria-label') ?? byField.textContent);
  }
  // A cell spanning columns has no single header.
  if ((cell as HTMLTableCellElement).colSpan > 1) return null;
  const span = (c: Element) => Math.max(1, (c as HTMLTableCellElement).colSpan || 1);
  let index = 0;
  for (const c of Array.from(row.children)) {
    if (c === cell) break;
    index += span(c);
  }
  const headers = Array.from(table.querySelectorAll('th, [role="columnheader"]')).filter(
    (h) => h.closest('tbody') === null,
  );
  const headerRow = headers[0]?.parentElement;
  if (headerRow === null || headerRow === undefined) return null;
  let start = 0;
  let header: Element | undefined;
  for (const h of headers.filter((x) => x.parentElement === headerRow)) {
    if (index < start + span(h)) {
      header = h;
      break;
    }
    start += span(h);
  }
  if (header === undefined || isPrivate(header)) return null;
  return clean(header.getAttribute('aria-label') ?? header.textContent);
}

/** The nearest heading above `el`: previous siblings first, then up the tree. */
function sectionHeading(el: Element): string | null {
  let node: Element | null = el;
  for (let depth = 0; node !== null && depth < 12; depth += 1) {
    let sib: Element | null = node.previousElementSibling;
    while (sib !== null) {
      const heading =
        roleOf(sib) === 'heading' ? sib : sib.querySelector('h1,h2,h3,h4,h5,h6,[role="heading"]');
      if (heading !== null) {
        if (isPrivate(heading)) return null;
        const text = clean(heading.textContent);
        if (text !== null) return text;
      }
      sib = sib.previousElementSibling;
    }
    node = node.parentElement;
    if (node === null || node === el.ownerDocument.body) break;
  }
  return null;
}

/**
 * Describe `target` by UI text, or null when it cannot be done without risking data.
 * Climbs a few ancestors from the click to the nearest element with a role and a name.
 */
export function describeElement(target: Element): AskDinkPick | null {
  const cell = target.closest(DATA_CELL);
  if (cell !== null) {
    const header = columnHeaderFor(cell);
    if (header === null) return null;
    return { label: header, role: 'column', section: sectionHeading(cell) };
  }

  if (isPrivate(target)) return null;
  let el: Element | null = target;
  for (let step = 0; el !== null && step < MAX_CLIMB; step += 1) {
    if (el === el.ownerDocument.body) break;
    const role = roleOf(el);
    if (role !== null) {
      if (isPrivate(el)) return null;
      const label = nameOf(el);
      if (label !== null) return { label, role, section: sectionHeading(el) };
    }
    el = el.parentElement;
  }
  return null;
}

/** The prefilled question for a pick (spec item 3). */
export function pickQuestion(pick: AskDinkPick, screenTitle: string | null): string {
  return `What does the "${pick.label}" ${pick.role} on ${screenTitle ?? 'this screen'} do?`;
}
